export const PRODUCTS_TOUR_ID = "tour-products";

const PRODUCT_INTRO_STORAGE_PREFIX = "sequelizer:product-intro:";
const PRODUCTS_TOUR_STORAGE_PREFIX = "sequelizer:onborda-products-tour:";

/** Seven days before the splash reappears after "Remind me later". */
export const PRODUCT_INTRO_REMIND_MS = 7 * 24 * 60 * 60 * 1000;

export type ProductIntroDismissal =
  | { readonly kind: "permanent" }
  | { readonly kind: "snooze"; readonly until: number };

function canUseLocalStorage(): boolean {
  return typeof globalThis.localStorage !== "undefined";
}

export function productIntroStorageKey(orgId: string): string {
  return `${PRODUCT_INTRO_STORAGE_PREFIX}${orgId}`;
}

export function productsTourStorageKey(orgId: string): string {
  return `${PRODUCTS_TOUR_STORAGE_PREFIX}${orgId}`;
}

export function readProductIntroDismissal(
  orgId: string,
): ProductIntroDismissal | null {
  if (!canUseLocalStorage() || !orgId.trim()) {
    return null;
  }

  const raw = localStorage.getItem(productIntroStorageKey(orgId));
  if (!raw) {
    return null;
  }

  if (raw === "permanent") {
    return { kind: "permanent" };
  }

  if (raw.startsWith("snooze:")) {
    const until = Number.parseInt(raw.slice("snooze:".length), 10);
    if (Number.isFinite(until)) {
      return { kind: "snooze", until };
    }
  }

  return null;
}

export function writeProductIntroDismissal(
  orgId: string,
  dismissal: ProductIntroDismissal,
): void {
  if (!canUseLocalStorage() || !orgId.trim()) {
    return;
  }

  const key = productIntroStorageKey(orgId);
  if (dismissal.kind === "permanent") {
    localStorage.setItem(key, "permanent");
    return;
  }

  localStorage.setItem(key, `snooze:${dismissal.until}`);
}

export function isProductIntroSuppressed(orgId: string): boolean {
  const dismissal = readProductIntroDismissal(orgId);
  if (!dismissal) {
    return false;
  }

  if (dismissal.kind === "permanent") {
    return true;
  }

  return Date.now() < dismissal.until;
}

export function isProductsTourCompleted(orgId: string): boolean {
  if (!canUseLocalStorage() || !orgId.trim()) {
    return false;
  }

  return (
    localStorage.getItem(productsTourStorageKey(orgId)) === "completed"
  );
}

export function markProductsTourCompleted(orgId: string): void {
  if (!canUseLocalStorage() || !orgId.trim()) {
    return;
  }

  localStorage.setItem(productsTourStorageKey(orgId), "completed");
}

export function isProductOnboardingRoute(pathname: string): boolean {
  const path = pathname.split("?")[0] ?? pathname;

  return (
    path.startsWith("/auth/new-onboarding") ||
    path.startsWith("/auth/onboarding") ||
    path.includes("/products/new") ||
    path.includes("/products/first")
  );
}
